import { useMemo } from 'react';

import { CataloguePickerParams, useCataloguePicker } from '@/hooks/use-catalogue-picker';
import { PaginatedResponse } from '@/types/api';

import { LogosFilterParams, useInfiniteLogos, useSelectedLogos } from '../api/get-logos';
import { logosQueryKeys } from '../api/query-keys';
import { TLogo, TLogosAgregations } from '../types';

type RangeParams = Omit<LogosFilterParams, 'search' | 'labelsIn'>;

/** A seeded answer comes from loaded pages, which carry no aggregation. */
const useSelectedLogosForPicker = (
  ids: string[],
  { initialData }: { initialData?: PaginatedResponse<TLogo> }
) =>
  useSelectedLogos(ids, {
    initialData: initialData as PaginatedResponse<TLogo, TLogosAgregations> | undefined,
  });

type UseLogoPickerParams = CataloguePickerParams & {
  /** Narrows the listed logos on the server; the current value is shown whatever its labels. */
  labelsIn?: string[];
  range?: RangeParams;
};

export function useLogoPicker({ labelsIn, range, ...picker }: UseLogoPickerParams) {
  const labelsKey = labelsIn?.length ? [...labelsIn].sort().join(',') : '';
  const params = useMemo(
    () => ({ ...range, labelsIn: labelsKey ? labelsKey.split(',') : undefined }),
    [range, labelsKey]
  );

  return useCataloguePicker<TLogo, LogosFilterParams>({
    ...picker,
    params,
    cacheKey: logosQueryKeys.all,
    useInfinite: useInfiniteLogos,
    useSelected: useSelectedLogosForPicker,
  });
}
